import React, { useEffect, useRef, useState } from "react";
import { FiMic, FiSquare, FiTrash2 } from "react-icons/fi";
import { recordingToWav } from "../../utils/recordedAudio";
import "./VoiceNotificationRecorder.css";

const release = (session) => {
  clearInterval(session.timer);
  session.stream?.getTracks().forEach((track) => track.stop());
};

export default function VoiceNotificationRecorder({ value, onChange, onBusyChange, disabled }) {
  const [phase, setPhase] = useState("idle");
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState("");
  const sessionRef = useRef(null);

  useEffect(() => {
    if (!value) { setPreview(""); return; }
    const url = URL.createObjectURL(value);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  useEffect(() => () => {
    const session = sessionRef.current;
    if (!session) return;
    session.cancelled = true;
    if (session.recorder && session.recorder.state !== "inactive") session.recorder.stop();
    release(session);
    sessionRef.current = null;
  }, []);

  const finish = (session) => {
    release(session);
    if (sessionRef.current === session) {
      sessionRef.current = null;
      setPhase("idle");
      onBusyChange(false);
    }
  };

  const start = async () => {
    if (sessionRef.current || disabled) return;
    setError("");
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setError("Microphone recording requires HTTPS or localhost. Open the secure admin website to record.");
      return;
    }
    if (!window.MediaRecorder) {
      setError("Recording is not supported in this browser. Try an up-to-date Chrome, Edge or Safari.");
      return;
    }
    const session = { chunks: [], cancelled: false };
    sessionRef.current = session;
    setPhase("starting"); setSeconds(0); onBusyChange(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });
      session.stream = stream;
      if (session.cancelled) { release(session); return; }
      const mime = ["audio/mp4", "audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/webm"].find((type) => window.MediaRecorder.isTypeSupported(type));
      const recorder = new window.MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      session.recorder = recorder;
      recorder.ondataavailable = (event) => { if (event.data.size && !session.cancelled) session.chunks.push(event.data); };
      recorder.onerror = () => {
        if (session.cancelled) return;
        session.cancelled = true;
        setError("Recording was interrupted. Please record your message again.");
        if (recorder.state !== "inactive") recorder.stop();
        finish(session);
      };
      recorder.onstop = async () => {
        release(session);
        if (session.cancelled) return;
        setPhase("processing");
        try {
          const blob = new Blob(session.chunks, { type: recorder.mimeType || mime });
          if (!blob.size) throw new Error("No audio was recorded. Please try again.");
          const wav = await recordingToWav(blob);
          if (!session.cancelled) onChange(new File([wav], `voice-${Date.now()}.wav`, { type: "audio/wav" }));
        } catch (err) {
          if (!session.cancelled) setError(err.message || "Unable to prepare the recording. Please try again.");
        } finally { finish(session); }
      };
      recorder.start(1000);
      session.startedAt = Date.now();
      session.timer = setInterval(() => setSeconds(Math.floor((Date.now() - session.startedAt) / 1000)), 250);
      setPhase("recording");
    } catch (err) {
      if (!session.cancelled) {
        setError(err.name === "NotAllowedError" ? "Microphone access was denied. Allow microphone access in your browser and try again."
          : err.name === "NotFoundError" ? "No microphone was found. Connect a microphone and try again."
          : "Unable to use the microphone. Check that it is connected and available.");
      }
      finish(session);
    }
  };

  const stop = () => {
    const session = sessionRef.current;
    if (session?.recorder?.state === "recording") {
      setPhase("processing");
      session.recorder.stop();
    }
  };

  const cancel = () => {
    const session = sessionRef.current;
    if (!session) return;
    session.cancelled = true;
    if (session.recorder && session.recorder.state !== "inactive") session.recorder.stop();
    finish(session);
    setError("");
  };

  const busy = phase !== "idle";
  const elapsed = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
  return (
    <div className="voice-notification-recorder">
      <p className="voice-record-help">Record a voice message with your microphone. You can listen before sending.</p>
      <div className="voice-record-controls">
        {!busy && <button type="button" className="voice-record-button" disabled={disabled} onClick={start}><FiMic aria-hidden="true" /> {value ? "Record Again" : "Record Audio"}</button>}
        {phase === "recording" && <>
          <span className="voice-record-status" role="status">Recording <time>{elapsed}</time></span>
          <button type="button" className="voice-record-button" onClick={stop}><FiSquare aria-hidden="true" /> Stop Recording</button>
        </>}
        {phase === "starting" && <span role="status">Waiting for microphone access…</span>}
        {phase === "processing" && <span role="status">Preparing your recording…</span>}
        {busy && <button type="button" className="voice-record-button secondary" onClick={cancel}>Cancel</button>}
        {!busy && value && <button type="button" className="voice-record-button secondary" disabled={disabled} onClick={() => { onChange(null); setError(""); }}><FiTrash2 aria-hidden="true" /> Remove</button>}
      </div>
      {!busy && preview && <div className="voice-record-preview"><audio key={preview} controls src={preview} aria-label="Preview voice notification" /><p>Recording attached to this notification.</p></div>}
      {error && <p className="voice-record-error" role="alert">{error}</p>}
    </div>
  );
}
